import { useAppStore } from "@hooks/useAppStore";
import useMixedItemDist from "@hooks/useMixedItemDist";
import {
	getAnsweredCount,
	getFallbackProficiencyLevelLabel,
	groupQuestionTypesBySkill,
} from "@memorang/helpers/language-learning";
import {
	QUESTION_TYPE_MASTERY_WINDOW,
	resolveMasteryForTagId,
} from "@memorang/helpers/language-learning-mastery";
import {
	isProficiencyTag,
	isQuestionTypeTag,
	isSkillTag,
} from "@memorang/helpers/tag";
import type { ConnectedLevel } from "@memorang/ui/components/ConnectedLevels";
import { toConnectedLevels } from "@memorang/ui/components/ConnectedLevels/helpers";
import type { ItemTypeStat } from "@memorang/ui/components/ItemTypeDetailModal/types";
import {
	buildProficiencyIconMap,
	getProficiencyLevelIcons,
	getQuestionTypeIcon,
	getSkillIcon,
} from "@memorang/ui/constants";
import type { LevelIconName } from "@memorang/ui/icon";
import { useTranslation } from "react-i18next";
import { useParsedRecommendationContext } from "./useParsedRecommendationContext";

export type ItemTypeDetail = {
	icon: LevelIconName;
	title: string;
	description?: string;
	levels: ConnectedLevel[];
	stats: ItemTypeStat[];
};

export const useItemTypeDetail = (
	questionTypeId?: string,
): ItemTypeDetail | undefined => {
	const { data: commonDistribution } = useMixedItemDist();
	const recommendationContext = useParsedRecommendationContext();
	const proficiencyLevelsConfig = useAppStore(
		(state) => state.app.proficiencyLevels,
	);
	const hideTotalItemCounts = useAppStore(
		(state) => state.app.hideTotalItemCounts,
	);
	const showProficiencyEstimates = useAppStore(
		(state) => state.reportView?.options?.showProficiencyEstimates,
	);
	const { t, i18n } = useTranslation();

	const tags = commonDistribution?.tags ?? [];
	const questionTypeTags = tags.filter(isQuestionTypeTag);
	const questionType = questionTypeTags.find(
		(tag) => String(tag.id) === questionTypeId,
	);
	if (!questionType) {
		return undefined;
	}

	const skillTags = tags.filter(isSkillTag);
	const proficiencyTags = tags.filter(isProficiencyTag);
	const rowsBySkill = groupQuestionTypesBySkill(skillTags, questionTypeTags);
	const skill = skillTags.find((candidate) =>
		(rowsBySkill.get(candidate.id) ?? []).some(
			(tag) => tag.id === questionType.id,
		),
	);

	const levelIcons = getProficiencyLevelIcons(proficiencyLevelsConfig);
	const { fills, displayLevel } = resolveMasteryForTagId(
		recommendationContext,
		questionType.id,
		QUESTION_TYPE_MASTERY_WINDOW,
		getFallbackProficiencyLevelLabel(proficiencyTags),
	);
	const levels = toConnectedLevels(fills, levelIcons);
	const currentLevelIconMap = buildProficiencyIconMap(proficiencyLevelsConfig);
	const answered = getAnsweredCount(questionType);
	const total = questionType.numItems ?? (questionType.items ?? []).length;
	const responses = questionType.totalResponses;
	const answeredStat: ItemTypeStat = hideTotalItemCounts
		? {
				icon: "content-copy",
				value: String(responses),
				label: t("dashboard.itemTypes.responsesLabel", { count: responses }),
			}
		: {
				icon: "card-multiple-outline",
				value:
					total > 0 && answered === total
						? t("dashboard.itemTypes.answeredAll")
						: t("dashboard.itemTypes.answeredPartial", { answered, total }),
				label: t("dashboard.itemTypes.answeredLabel_other"),
			};

	const stats: ItemTypeStat[] = [
		...(skill
			? [
					{
						icon: getSkillIcon(skill.title),
						value: skill.title,
						label: t("dashboard.itemTypes.detail.skillLabel"),
					},
				]
			: []),
		{
			icon:
				(displayLevel ? currentLevelIconMap[displayLevel] : undefined) ??
				"school-outline",
			value: displayLevel ?? "",
			label: t("dashboard.itemTypes.detail.estimatedLevelLabel"),
		},
		answeredStat,
	];

	const descriptionKey = `questionTypes.descriptions.${questionType.title}`;

	return {
		icon: getQuestionTypeIcon(questionType.title),
		title: questionType.title,
		description: i18n.exists(descriptionKey) ? t(descriptionKey) : undefined,
		levels: showProficiencyEstimates ? levels : [],
		stats,
	};
};
